import React from 'react';
import PropTypes from 'prop-types';

import { useQuery, useQueryClient, useMutation } from '@ubisend/pulse-hooks';
import { CreatableSelect, formatSelectOption } from '@ubisend/pulse-components';
import { useAuth } from '@ubisend/pulse-auth';

import { createGroup as createGroupApi } from '../api/index';

const GroupSelect = ({
  value,
  onChange,
  filteredGroupIds = [],
  for: scope = 'tickets',
  sortAlphabetically = false,
  ...props
}) => {
  const queryClient = useQueryClient();

  const { hasFeature } = useAuth();

  const { data, isLoading, isSuccess } = useQuery({
    queryKey: ['groups', { scoped_to: scope }]
  });

  const createGroup = useMutation({
    mutationFn: createGroupApi,
    onSuccess: () => {
      queryClient.invalidateQueries(['groups', { scoped_to: scope }]);
    }
  });

  const sortData = data => {
    let groups = data.data;

    if (filteredGroupIds && filteredGroupIds.length > 0) {
      groups = groups.filter(group => filteredGroupIds.includes(group.id));
    }

    if (sortAlphabetically) {
      groups.sort((a, b) =>
        a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
      );
    }

    if (!hasFeature('small talk')) {
      groups = groups.filter(group => !group.name.includes('Small talk'));
    }

    return groups.map(formatSelectOption);
  };

  const handleGroupChange = async initialOptions => {
    const items = initialOptions || [];

    const groups = await Promise.all(
      items.map(async ({ __isNew__: isNew, ...item }) => {
        if (isNew) {
          const { data } = await createGroup.mutateAsync({
            name: item.value,
            scoped_to: scope
          });
          return { value: data.data.id, label: data.data.name };
        }

        return item;
      })
    );

    onChange(groups);
  };

  return (
    <CreatableSelect
      closeMenuOnSelect
      isMulti
      isClearable
      isLoading={isLoading}
      options={isSuccess ? sortData(data) : []}
      onChange={handleGroupChange}
      placeholder="Tags"
      value={
        isSuccess &&
        value &&
        value
          .map(id => {
            const group = data.data.find(group => group.id === id);
            return group ? formatSelectOption(group) : null;
          })
          .filter(Boolean)
      }
      {...props}
    />
  );
};

GroupSelect.propTypes = {
  value: PropTypes.array.isRequired,
  sortAlphabetically: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  filteredGroupIds: PropTypes.array,
  for: PropTypes.oneOf(['faqs', 'tickets', 'conversations', 'links', 'files'])
};

export default GroupSelect;
